import { intervalToDuration } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isCountdownTooLarge } from './validators'

export function useCountdown(
  end: number,
  showSeconds: boolean,
  { onElapse }: { onElapse?: () => void }
): { countdownText: string | null; countdownTooLarge: boolean } {
  const date = useMemo(() => new Date(end), [end])
  const [cancelPending, setCancelPending] = useState<boolean>(false)
  const [now, setNow] = useState(new Date())

  const countdownTooLarge = useMemo(() => isCountdownTooLarge(date, now), [date, now])

  const tick = useCallback(() => {
    setNow(new Date())

    const nextTick = setTimeout(() => {
      if (!countdownTooLarge && !cancelPending) tick()
    }, 1000)

    return () => {
      clearTimeout(nextTick)
    }
  }, [countdownTooLarge, cancelPending])

  useEffect(() => {
    if (!cancelPending) tick()
    return () => {
      setCancelPending(true)
    }
  }, [tick, cancelPending])

  useEffect(() => {
    if (!cancelPending && onElapse && date.getTime() <= now.getTime()) {
      onElapse()
    }
  }, [onElapse, date, now, cancelPending])

  const duration = countdownTooLarge
    ? null
    : intervalToDuration({
        start: now,
        end: date,
      })

  const countdownText = useMemo(() => {
    const times = duration
      ? [
          duration.months ? duration.months + 'mo' : null,
          duration.days ? duration.days + 'd' : null,
          duration.hours + 'h',
          duration.minutes + 'm',
          duration.seconds + 's',
        ].filter(Boolean)
      : []
    // Remove seconds (i.e. last element in the array) and cap to the first 3 time blocks
    return !showSeconds ? times.slice(0, -1).slice(0, 3).join(' ') : times.join(' ')
  }, [duration, showSeconds])

  return { countdownText, countdownTooLarge }
}
